<script>
  // Каркас LC-цепочки входа — перенос машины экранов из src_ui/web/src/App.tsx
  // (только вход-часть: auth → characters → identity/appearance → done → spawn).
  // Протокол ТОТ ЖЕ: данные ← window 'message', действия → nuiPost ('lc:nui').
  // HUD/часы/инвентарь/тосты из App.tsx сюда НЕ переносятся — ими владеет src_cef.
  import { onMount, onDestroy } from 'svelte';
  import { nuiPost } from './nui';
  import './lc.css';
  import musicUrl from './music.mp3';
  import Auth from './auth.svelte';
  import Loading from './loading.svelte';
  import Characters from './characters.svelte';
  import Identity from './identity.svelte';
  import Appearance from './appearance.svelte';
  import Done from './done.svelte';
  import Spawn from './spawn.svelte';

  let screen = 'hidden'; // hidden | auth | characters | spawn | identity | appearance | done
  let known = null;
  let quick = false;
  let username = null;
  // slotStates: 'char' | 'free' | 'locked' — платные слоты приходят с сервера как 'locked'
  let roster = { chars: [], slots: 3, slotStates: undefined };
  let selectedChar = null;
  let spawning = false;     // идёт облёт камеры к телу — прячем UI спавна
  let loadOverlay = false;  // экран загрузки поверх (стрим текстур/полёт камеры)
  let authResult = null;
  let loading = null;
  let identity = null;
  let outfitLabel = 'Кэжуал';
  let charError = '';

  // ── фоновая музыка меню — играет от входа до спавна (дословно App.tsx) ──
  let bgm = null;
  let bgmFade = null;
  // кнопка «вырубить музыку» (справа внизу); выбор игрока переживает перезаход
  let bgmMuted = (() => { try { return localStorage.getItem('lc:bgmMuted') === '1'; } catch (e) { return false; } })();
  $: { if (bgm) bgm.muted = bgmMuted; try { localStorage.setItem('lc:bgmMuted', bgmMuted ? '1' : '0'); } catch (e) { /* нет localStorage */ } }
  const fadeMusic = (to, ms, thenPause = false) => {
    const a = bgm;
    if (!a) return;
    if (bgmFade) clearInterval(bgmFade);
    const from = a.volume;
    const start = Date.now();
    bgmFade = setInterval(() => {
      const k = Math.min(1, (Date.now() - start) / ms);
      a.volume = Math.max(0, Math.min(1, from + (to - from) * k));
      if (k >= 1) { if (bgmFade) clearInterval(bgmFade); bgmFade = null; if (thenPause) a.pause(); }
    }, 40);
  };

  $: inMenu = screen !== 'hidden' || loadOverlay;
  $: if (bgm) {
    if (!inMenu) fadeMusic(0, 1500, true); // вышел в игру — мягко гасим за 1.5с
    else if (bgm.paused) {
      bgm.volume = 0;
      const start = () => bgm.play().then(() => fadeMusic(0.3, 2000)); // громкость 30%, появление за 2с
      start().catch(() => {
        // CEF/браузер зарезал автозапуск звука — повторяем с первого жеста игрока
        const kick = () => {
          start().catch(() => { /* игрок успел выйти в мир */ });
          window.removeEventListener('pointerdown', kick);
          window.removeEventListener('keydown', kick);
        };
        window.addEventListener('pointerdown', kick);
        window.addEventListener('keydown', kick);
      });
    }
  }

  // авто-масштаб экранов авторизации/создания под разрешение (на 1440p/4K не мелко)
  let uiScale = 1;
  const calcScale = () => { uiScale = Math.max(1, (window.innerHeight / 1080) * 1.1); };

  // ── приём сообщений от клиента (дословный switch App.tsx, только вход-цепочка) ──
  const onMsg = (e) => {
    const msg = e.data;
    switch (msg?.type) {
      case 'show':
        screen = msg.screen ?? 'auth';
        break;
      case 'welcome':
        // показываем меню входа: профиль найден, можно войти в один клик БЕЗ пароля,
        // но галочку «запомнить» можно снять (автоматом больше не проскакиваем)
        known = msg.known ?? null;
        quick = !!msg.quick;
        break;
      case 'authResult':
        loading = null;
        authResult = msg;
        if (msg.username) username = msg.username;
        // регистрация → сразу создание персонажа; вход → накрываем загрузкой и ждём ростер
        if (msg.ok && msg.mode === 'register') screen = 'identity';
        else if (msg.ok) loadOverlay = true; // вход: оверлей сразу, чтобы ростер не мелькнул
        break;
      case 'roster': // список персонажей после входа
        loading = null;
        roster = { chars: msg.chars ?? [], slots: msg.slots ?? 3, slotStates: msg.slotStates };
        screen = 'characters';
        loadOverlay = false; // список готов — снимаем загрузку
        break;
      case 'created': {
        loading = null;
        const c = msg.char ?? null;
        if (c) selectedChar = { ...c, idx: msg.idx ?? 0, cash: c.cash ?? 250, bank: c.bank ?? 1000 };
        screen = 'done';
        break;
      }
      case 'charError':
        loading = null;
        charError = msg.error ?? 'Не удалось создать персонажа';
        break;
      case 'hide':
        loading = null;
        spawning = false;
        screen = 'hidden';
        // ⚠️ Заставку здесь НЕ снимаем: при входе в мир её включают специально —
        // под ней меняются погода и время и летит камера. Гасит её только
        // сообщение load:false от клиента (dive/finishDive).
        break;
      case 'load': // внутриигровой экран загрузки поверх (полёт камеры / стрим текстур)
        loadOverlay = !!msg.on;
        break;
      case 'spawnBack': // ESC на выборе точки спавна — назад к выбору персонажа
        spawning = false;
        screen = 'characters';
        break;
    }
  };

  onMount(() => {
    const a = new Audio(musicUrl);
    a.loop = true;
    a.volume = 0;
    bgm = a;
    calcScale();
    window.addEventListener('resize', calcScale);
    window.addEventListener('message', onMsg);
  });
  onDestroy(() => {
    if (bgm) bgm.pause();
    if (bgmFade) clearInterval(bgmFade);
    window.removeEventListener('resize', calcScale);
    window.removeEventListener('message', onMsg);
  });

  // сервер попросил экран спавна, а выбранный персонаж не доехал (рассинхрон) —
  // берём того, кто выбран в ростере последним
  $: spawnChar = selectedChar ?? roster.chars[0] ?? null;
</script>

<div class="lcx">
  {#if screen === 'characters'}
    <!-- выбор персонажа и точки спавна — собственная полноэкранная вёрстка (без zoom) -->
    <Characters
      chars={roster.chars}
      slots={roster.slots}
      slotStates={roster.slotStates}
      username={username ?? known}
      onPlay={(c) => { selectedChar = c; screen = 'spawn'; }}
      onCreate={() => { nuiPost('createChar', {}); }}
    />
    <Loading show={loadOverlay} />
  {:else if screen === 'spawn' && spawnChar}
    {#if spawning}
      <!-- облёт к телу — чистый экран (+оверлей при нужде) -->
      <Loading show={loadOverlay} />
    {:else}
      <Spawn
        char={spawnChar}
        onBack={() => { screen = 'characters'; }}
        onConfirm={(pos, place) => {
          if (spawning) return;
          spawning = true;
          fadeMusic(0, 1500, true); // выбрал точку — музыка плавно гаснет за 1.5с
          nuiPost('spawnConfirm', { char: selectedChar, idx: selectedChar.idx, pos, place });
        }}
      />
      <Loading show={loadOverlay} />
    {/if}
  {:else if screen !== 'hidden'}
    <div style="zoom:{uiScale}">
      <div class="statuspill"><span class="dot"></span>Сервер онлайн</div>

      {#if screen === 'auth'}
        <Auth
          {known}
          {quick}
          result={authResult}
          onLoading={(text) => { loading = text; authResult = null; }}
        />
      {/if}

      {#if screen === 'identity'}
        <Identity
          onDone={(id) => { identity = id; screen = 'appearance'; }}
          onBack={() => { charError = ''; screen = 'characters'; }}
        />
      {/if}

      {#if screen === 'appearance'}
        <Appearance
          gender={identity?.gender ?? 'm'}
          error={charError}
          clearError={() => { charError = ''; }}
          onBack={() => { screen = 'identity'; }}
          onFinish={(outfit) => { outfitLabel = outfit; loading = 'Сохранение персонажа'; }}
        />
      {/if}

      {#if screen === 'done' && identity}
        <Done
          {identity}
          {outfitLabel}
          onEnter={() => { if (selectedChar) screen = 'spawn'; }}
        />
      {/if}

      {#if loading}
        <div class="loading">
          <div class="spin"></div>
          <div class="txt">{loading}</div>
        </div>
      {/if}

      <div class="buildtag">JUST RP · build 0.0.1</div>
    </div>
    <Loading show={loadOverlay} />
  {:else}
    <Loading show={loadOverlay} />
  {/if}

  {#if inMenu}
    <!-- аккуратная кнопка звука справа внизу — на всех экранах входа до спавна -->
    <div
      on:click={() => { bgmMuted = !bgmMuted; }}
      title={bgmMuted ? 'Включить музыку' : 'Выключить музыку'}
      style="position:fixed;right:24px;bottom:56px;z-index:60;width:40px;height:40px;border-radius:12px;display:flex;align-items:center;justify-content:center;background:rgba(10,9,8,0.8);border:1px solid rgba(255,255,255,0.14);color:{bgmMuted ? 'rgba(255,255,255,0.45)' : '#e0a83e'};cursor:pointer"
    >
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round">
        <path d="M11 5 6 9H3v6h3l5 4z" />
        {#if bgmMuted}<path d="M16 9l6 6M22 9l-6 6" />{:else}<path d="M15.5 8.5a5 5 0 0 1 0 7M18.5 5.5a9 9 0 0 1 0 13" />{/if}
      </svg>
    </div>
  {/if}
</div>
